import NiceModal from "@ebay/nice-modal-react"
import cx from "classnames"
import { ArrowUpRight, Bot, GitFork, Lightbulb, Loader2, MessageSquare, Play, ShieldCheck, type LucideIcon } from "lucide-react"
import { formatDuration, formatTaskCost } from "../../persistence/taskStatsUtils"
import type { TaskSessionDebugInfo } from "../../store/TaskModel"
import { isTaskSessionActive } from "./taskSessionTree"
import { TaskSessionConversationModal } from "./TaskSessionConversationModal"
import { HARNESS_META } from "../../modelRegistry"
import { useOptionalCodeStore } from "../../store/context"
import { AppToolsControl } from "./AppToolsControl"

interface TaskSessionCardProps {
    taskId: string
    session: TaskSessionDebugInfo
    taskSessions?: readonly TaskSessionDebugInfo[]
    childCount?: number
}

type TaskSessionOrigin = "primary" | "council" | "review" | "preview" | "delegation" | "session"

const ORIGIN_PRESENTATION: Record<TaskSessionOrigin, { label: string; icon: LucideIcon }> = {
    primary: { label: "Main session", icon: MessageSquare },
    council: { label: "Started by Council", icon: Lightbulb },
    review: { label: "Started by Review", icon: ShieldCheck },
    preview: { label: "Started by Preview", icon: Play },
    delegation: { label: "Delegated", icon: GitFork },
    session: { label: "Sub-agent session", icon: Bot },
}

export function TaskSessionCard({ taskId, session, taskSessions, childCount }: TaskSessionCardProps): JSX.Element {
    const store = useOptionalCodeStore()
    const workspaceId = store?.tasks.getTask(taskId)?.repoId
    const active = isTaskSessionActive(session.status)
    const resolvedChildCount = childCount ?? taskSessions?.filter((candidate) => candidate.parentSessionId === session.nestSessionId).length ?? 0
    const origin = taskSessionOrigin(session)
    const originPresentation = ORIGIN_PRESENTATION[origin]
    const OriginIcon = originPresentation.icon
    const title = taskSessionTitle(session)
    const subtitle = taskSessionSubtitle(session)
    const preview = session.lastInputPreview ?? session.lastEventPreview

    const openConversation = () => {
        void NiceModal.show(TaskSessionConversationModal, {
            taskId,
            session,
            taskSessions,
        })
    }

    return (
        <section className="flex min-w-0 flex-wrap border border-border bg-base-100">
            <button
                type="button"
                className="btn group flex h-10 min-w-0 flex-1 items-center gap-2 bg-base-100 px-2.5 text-left text-base-content transition-colors hover:bg-base-200 max-[900px]:h-11"
                onClick={openConversation}
                aria-haspopup="dialog"
                aria-label={`Open ${title} conversation`}
                title={preview}
            >
                <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center text-primary" title={originPresentation.label}>
                    <OriginIcon size={13} aria-hidden="true" />
                    <span className="sr-only">{originPresentation.label}</span>
                </span>
                <span className="min-w-0 flex-1 truncate text-xs">
                    <span className="font-semibold text-base-content">{title}</span>
                    {subtitle && <span className="text-muted"> · {subtitle}</span>}
                </span>
                <span className="inline-flex shrink-0 items-center gap-2 text-[10px] text-muted">
                    {session.usage?.costAvailable && <span className="tabular-nums max-[640px]:hidden">{formatTaskCost(session.usage.costUsd)}</span>}
                    {session.usage && session.usage.durationMs > 0 && (
                        <span className="tabular-nums max-[640px]:hidden">{formatDuration(session.usage.durationMs)}</span>
                    )}
                    {resolvedChildCount > 0 && (
                        <span className="max-[640px]:hidden" title={`${resolvedChildCount} child session${resolvedChildCount === 1 ? "" : "s"}`}>
                            {resolvedChildCount} child{resolvedChildCount === 1 ? "" : "ren"}
                        </span>
                    )}
                    <span className="inline-flex items-center gap-1.5 font-medium capitalize">
                        {active ? (
                            <Loader2 size={11} className="animate-spin text-primary" />
                        ) : (
                            <span className={cx("h-1.5 w-1.5", statusDotClass(session.status))} />
                        )}
                        {session.status ?? "unknown"}
                    </span>
                    <ArrowUpRight size={11} className="text-base-content group-hover:text-primary" aria-hidden="true" />
                </span>
            </button>
            {workspaceId && session.nestSessionId && <AppToolsControl identity={{ taskId, workspaceId, sessionId: session.nestSessionId }} inline />}
        </section>
    )
}

export function taskSessionTitle(session: TaskSessionDebugInfo): string {
    if (session.label) return session.label
    if (session.role === "primary") return "Primary"
    return `${HARNESS_META[session.harnessId].name} sub-agent`
}

export function taskSessionSubtitle(session: TaskSessionDebugInfo): string {
    const harness = HARNESS_META[session.harnessId].name
    return [harness, session.model, session.thinking, session.mode === "read-only" ? "read-only" : undefined].filter(Boolean).join(" · ")
}

export function taskSessionOrigin(session: TaskSessionDebugInfo): TaskSessionOrigin {
    if (session.role === "primary") return "primary"

    const label = session.label?.toLowerCase() ?? ""
    if (/\b(council|counsel|adviser|advisor)\b/.test(label)) return "council"
    if (/\b(review|reviewer|audit|critic|critique)\b/.test(label)) return "review"
    if (/\bpreview\b/.test(label)) return "preview"
    if (/\b(delegate|delegated|delegation|implementer|worker)\b/.test(label)) return "delegation"
    if (session.mode && session.mode !== "read-only") return "delegation"
    return "session"
}

function statusDotClass(status: string | undefined): string {
    if (status === "failed" || status === "error") return "bg-error"
    if (status === "cancelled" || status === "stopped") return "bg-warning"
    if (status === "idle" || status === "completed") return "bg-success"
    return "bg-muted"
}
